import { catchError, map } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import * as i0 from "@angular/core";
import * as i1 from "./api.service";
import * as i2 from "./helper.service";
/**
 * Template keys currently exposed by the back-end
 * (@see CONFIG.AVAILABLE_PRIVACY_DOCUMENT_TEMPLATES). Add a new constant
 * here whenever a new template is added back-end side.
 */
export var DEFAULT_PRIVACY_DOCUMENT_TEMPLATE = 'all';
export var PROPERTY_PRIVACY_TEMPLATE = 'informativa_privacy_immobile';
export var SUPPLIER_PRIVACY_TEMPLATE = 'informativa_privacy_fornitore';
export var LANDLORD_PRIVACY_TEMPLATE = 'informativa_privacy_conduttore_immobiliari';
export var COMMON_AREAS_PRIVACY_TEMPLATE = 'informativa_privacy_conduttore_parti_comuni';
export var EMPLOYEE_PRIVACY_TEMPLATE = 'informativa_privacy_dipendente';
export var DATA_PROCESSOR_APPOINTMENT_TEMPLATE = 'nomina_responsabile_trattamento_dati';
/**
 * Template keys for company-level ("studio") privacy documents: these are
 * not tied to any property, they are filled only with the company's own
 * registry data (@see CONFIG.AVAILABLE_STUDIO_PRIVACY_DOCUMENT_TEMPLATES)
 */
export var STUDIO_EMPLOYEE_PRIVACY_TEMPLATE = 'informativa_privacy_dipendenti_studio';
export var STUDIO_SUBPROCESSOR_APPOINTMENT_TEMPLATE = 'nomina_sub_responsabile_trattamento';
export var STUDIO_AUTHORIZED_PERSON_APPOINTMENT_TEMPLATE = 'nomina_soggetto_autorizzato_trattamento';
export var STUDIO_CONFIDENTIALITY_AGREEMENT_TEMPLATE = 'patto_di_riservatezza';
export var STUDIO_PEC_DELIVERY_DELEGATION_TEMPLATE = 'delega_trasmissione_pec';
/**
 * Maps the mime types returned by generateDocuments/generateStudioDocument to
 * their file extension. The number of documents actually bundled (and thus
 * whether the response is a single file or a .zip) depends on templateType
 * and how many properties were requested, not just on the property count, and
 * the format of a single file depends on the property's subscription: the
 * notices of a Privacy SAG property (condo-owner, supplier, both tenant ones)
 * come back as PDF, everything else as Word. Callers MUST therefore derive
 * the extension from the blob's real mime type rather than from
 * propertyIds.length or templateType.
 */
export var PRIVACY_DOC_MIME_EXTENSIONS = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'application/x-zip-compressed': 'zip',
};
export var DEFAULT_PRIVACY_DOC_EXTENSION = 'docx';
var PrivacyDocsService = /** @class */ (function () {
    /**
     * Class constructor
     */
    function PrivacyDocsService(apiService, helperService) {
        this.apiService = apiService;
        this.helperService = helperService;
    }
    /**
     * Get privacy docs list for given company and query params
     *
     * @since 1.0.0
     *
     * @param companyId Company Id
     * @param [start] Query left offset
     * @param [limit] Results number
     * @param [sort] Sorting field name
     * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
     * @param [search] Filtering object with filtering data
     */
    PrivacyDocsService.prototype.getList = function (companyId, start, limit, sort, sortVersus, search) {
        var params = { start: start, limit: limit, sort: sort, sortVersus: sortVersus };
        if (search) {
            Object.keys(search).forEach(function (key) {
                var newParamKey = "search[" + key + "]";
                var newParamValue = search[key];
                params[newParamKey] = newParamValue;
            });
        }
        return this.apiService.apiCall('GET', "companies/privacy-docs/" + companyId, params);
    };
    /**
     * Get the percentage of properties/suppliers that already have the given
     * privacy doc template generated for this company (@see PROPERTY_PRIVACY_TEMPLATE,
     * SUPPLIER_PRIVACY_TEMPLATE, ...)
     *
     * @since 1.0.0
     *
     * @param companyId Company Id
     * @param templateType Back-end template key
     */
    PrivacyDocsService.prototype.getPercentage = function (companyId, templateType) {
        return this.apiService.apiCall('GET', "companies/privacy-docs/" + companyId + "/percentage/" + templateType);
    };
    /**
     * Trigger the generation of privacy docs for the given properties and
     * return the resulting file as a Blob: a single file (.docx, or .pdf for the
     * notices of a Privacy SAG property) when one document is generated, a .zip
     * bundle otherwise (@see PRIVACY_DOC_MIME_EXTENSIONS)
     *
     * @since 1.0.0
     *
     * @param companyId Company Id
     * @param propertyIds Ids of the properties to generate the document for (1 to 50, must belong to companyId)
     * @param templateType Back-end template key
     */
    PrivacyDocsService.prototype.generateDocuments = function (companyId, propertyIds, templateType) {
        var _this = this;
        if (templateType === void 0) { templateType = DEFAULT_PRIVACY_DOCUMENT_TEMPLATE; }
        return this.apiService.apiCall('POST', "companies/" + companyId + "/privacy-documents", { propertyIds: propertyIds, templateType: templateType }, { responseType: 'blob' }, undefined, undefined, 'v2').pipe(catchError(function (err) { return _this.helperService.parseErrorBlob(err); }));
    };
    /**
     * Trigger the generation of a company-level ("studio") privacy document,
     * filled only with this company's own registry data (no property
     * involved), and return the resulting editable Word file as a Blob
     *
     * @since 1.0.0
     *
     * @param companyId Company Id
     * @param templateType Back-end template key (@see STUDIO_*_TEMPLATE constants)
     */
    PrivacyDocsService.prototype.generateStudioDocument = function (companyId, templateType) {
        var _this = this;
        return this.apiService.apiCall('POST', "companies/" + companyId + "/studio-privacy-documents", { templateType: templateType }, { responseType: 'blob' }, undefined, undefined, 'v2').pipe(catchError(function (err) { return _this.helperService.parseErrorBlob(err); }));
    };
    /**
     * Public (unauthenticated) download of a property's privacy document(s),
     * resolved server-side from companyId + docType + taxCode. The
     * back-end must independently re-validate that the tax code belongs to
     * a property of this company before generating the file(s).
     * Returns an array (base64-encoded files) rather than a single blob
     * because a taxCode can resolve to more than one document.
     *
     * @since 1.0.0
     *
     * @param companyId Company Id
     * @param docType Public doc type ('condo-owner' or 'supplier')
     * @param taxCode Property tax code entered by the visitor
     * @param [masterId] For docType 'supplier', the Master (administrator company) the
     *                    tax code must be checked against; required by the back-end in that case
     * @param [supplierId] For docType 'supplier', present only when the public access page
     *                      was reached via a link scoped to a specific supplier (":supplierId"
     *                      route param, @see PrivacyDocsPublicAccessComponent)
     */
    PrivacyDocsService.prototype.downloadPublicPropertyPrivacyDoc = function (companyId, docType, taxCode, masterId, supplierId) {
        return this.apiService.apiCall('GET', "companies/" + companyId + "/privacy-docs/public/" + docType + "/download", { taxCode: taxCode, masterId: masterId, supplierId: supplierId }).pipe(map(function (response) { return (response && response.data) || []; }));
    };
    /**
     * Public (unauthenticated) list of Masters (administrator companies) a
     * supplier can pick from when downloading the "supplier" privacy doc for
     * the given company: the download must then be scoped to the selected
     * Master (@see downloadPublicPropertyPrivacyDoc).
     * Signature matches the "autocomplete" formly field's search$ contract
     * (@see AutocompleteTypeComponent) in its first 5 params, so it can be
     * bound directly to it; the trailing "supplierId" param is filled by the
     * caller's own closure, not by the autocomplete component.
     *
     * @since 1.0.0
     *
     * @param [start] Query left offset
     * @param [limit] Results number
     * @param [sort] Sorting field name
     * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
     * @param [search] Filtering object with filtering data
     * @param [supplierId] Present only when the public access page was reached via a link
     *                      scoped to a specific supplier (":supplierId" route param,
     *                      @see PrivacyDocsPublicAccessComponent), narrows the Masters list to that supplier
     */
    PrivacyDocsService.prototype.getPublicSupplierMasters = function (start, limit, sort, sortVersus, search, supplierId) {
        var params = { start: start, limit: limit, sort: sort, sortVersus: sortVersus, supplierId: supplierId };
        if (search) {
            Object.keys(search).forEach(function (key) {
                var newParamKey = "search[" + key + "]";
                var newParamValue = search[key];
                params[newParamKey] = newParamValue;
            });
        }
        if (supplierId) {
            return this.apiService.apiCall('GET', "companies/privacy-docs/public/" + supplierId + "/get-masters", params);
        }
        return this.apiService.apiCall('GET', "companies/privacy-docs/public/get-masters", params);
    };
    PrivacyDocsService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function PrivacyDocsService_Factory() { return new PrivacyDocsService(i0.ɵɵinject(i1.ApiService), i0.ɵɵinject(i2.HelperService)); }, token: PrivacyDocsService, providedIn: "root" });
    return PrivacyDocsService;
}());
export { PrivacyDocsService };
